import React from 'react';
import { AppLegalDoc, LegalIntro, LegalSection, LegalP, LegalList, LegalLink } from '../components/AppLegalDoc';

function AppPrivacyPage() {
  return (
    <AppLegalDoc
      docTitle="Turf Umpire: Privacy policy"
      updated="23 October 2026"
      seoTitle="Turf Umpire — Privacy policy"
      seoDescription="Privacy policy for Turf Umpire, the cricket scoring and umpiring app by Web Int Projects for Malda Cricket & Football Turf. Everything stays on your phone."
      seoPath="/app/privacy-policy"
      testId="app-privacy-page"
    >
      <LegalIntro>
        Turf Umpire is a scoring and umpiring app for turf and gully cricket. It is made by Web Int Projects, for Malda
        Cricket &amp; Football Turf. This page explains what the app keeps and what it does with it.
      </LegalIntro>

      <LegalSection title="In short">
        <LegalP>
          Everything you enter stays on your phone. The app has no server of its own, shows no ads and has no tracking
          or analytics. We can't see your matches.
        </LegalP>
      </LegalSection>

      <LegalSection title="What the app keeps on your phone">
        <LegalList
          items={[
            'Matches you score: team names, player names, every ball, the result and the date.',
            'The team lists and house rules from your last match, so the next one is quicker to set up.',
            'Your settings, such as vibration, and your best quiz scores.',
            'If you sign in with Google: your name and email address.',
          ]}
        />
        <LegalP>All of this is in the app's private storage. Other apps on the phone can't read it.</LegalP>
      </LegalSection>

      <LegalSection title="Signing in with Google">
        <LegalP>
          Signing in is optional. Google's sign-in screen gives the app your name and email address. The app uses them
          only to show your name in the app and on scorecards you share. It doesn't send them anywhere.
        </LegalP>
        <LegalP>
          Google's privacy policy covers the sign-in itself:{' '}
          <LegalLink href="https://policies.google.com/privacy">https://policies.google.com/privacy</LegalLink>
        </LegalP>
      </LegalSection>

      <LegalSection title="Sharing a scorecard">
        <LegalP>
          When you tap Share, the app makes a picture or a text message of the scorecard and opens Android's share
          menu. You choose where it goes, for example a WhatsApp group. After that, the app you sent it with has its own
          privacy policy. Nothing is shared unless you tap Share.
        </LegalP>
      </LegalSection>

      <LegalSection title="Permissions">
        <LegalP>
          The app only asks Android for vibration, for the buzz before the last ball of an over. It doesn't use your
          location, contacts, camera, microphone or files, and it has no internet access of its own: Google sign-in and
          app updates go through Google Play services.
        </LegalP>
      </LegalSection>

      <LegalSection title="Phone backups">
        <LegalP>
          If backup is turned on in your phone's settings, Android can include the app's matches, team lists and
          settings in your phone's own backup to your Google account, so they come back on a new phone. That backup
          belongs to your Google account and we can't see it. You can turn it off in your phone's Settings, under
          Google, then Backup.
        </LegalP>
      </LegalSection>

      <LegalSection title="App updates">
        <LegalP>
          If you installed the app from Google Play, it asks Google Play whether a newer version is out, so it can offer
          you the update. Google Play handles that check.
        </LegalP>
      </LegalSection>

      <LegalSection title="Children">
        <LegalP>
          Anyone can use the app, but it isn't made specially for children. Since nothing leaves your phone, we don't
          collect data from anyone, children included.
        </LegalP>
      </LegalSection>

      <LegalSection title="Deleting your data">
        <LegalP>
          Open Profile and tap Delete all my data to remove everything at once. Signing out removes your name and email.
          Uninstalling the app also deletes all of its data.
        </LegalP>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <LegalP>
          If the app ever keeps data online, for example a backup of your matches, we'll update this page and tell you
          in the app first. The date at the top shows when it last changed.
        </LegalP>
      </LegalSection>

      <LegalSection title="Contact">
        <LegalP>
          Questions or requests: <LegalLink href="mailto:maldaturf@gmail.com">maldaturf@gmail.com</LegalLink>
        </LegalP>
        <LegalP>Imagination and distribution by Web Int Projects, for Malda Cricket &amp; Football Turf.</LegalP>
      </LegalSection>
    </AppLegalDoc>
  );
}

export default AppPrivacyPage;
